/**
 * lib/receiptPdf.ts
 * Branded A4 payment receipt PDF (jsPDF), generated from a Payment.
 */
import { jsPDF } from 'jspdf';
import type { CompanySettings, Payment, PaymentMethod } from '@/types';
import { formatPula } from './money';
import { drawDocGlyph, drawWatermark, brandName } from './pdfIcons';

const ORANGE: [number, number, number] = [255, 107, 53];
const CHAR: [number, number, number] = [34, 39, 46];
const GRAY: [number, number, number] = [120, 128, 138];

const METHOD_LABEL: Record<PaymentMethod, string> = {
  bank_transfer: 'Bank transfer',
  cash: 'Cash',
  cheque: 'Cheque',
  card: 'Card',
  mobile: 'Mobile money',
  other: 'Other',
};

function fmtDate(iso: string): string {
  return iso ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
}

export function receiptFileName(p: Payment): string {
  return `Receipt-${p.receiptNumber}.pdf`;
}

export function buildReceiptPdf(p: Payment, company: CompanySettings): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210;
  const M = 15;
  const right = W - M;

  // Header band (inset within print margins, not full-bleed)
  const bandY = 12;
  const bandH = 26;
  const bandW = W - 2 * M;
  doc.setFillColor(...ORANGE);
  doc.rect(M, bandY, bandW, bandH, 'F');
  const cy = bandY + bandH / 2;
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(1.4);
  doc.setLineCap('round');
  doc.setLineJoin('round');
  const cW = 3.4;
  const chX = M + 8;
  doc.line(chX, cy - 3.4, chX + cW, cy);
  doc.line(chX + cW, cy, chX, cy + 3.4);
  const wordX = chX + cW + 3;
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(brandName(company), wordX, cy - 0.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('PROPRIETARY LIMITED', wordX, cy + 4.5);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('RECEIPT', M + bandW - 6, cy + 1.5, { align: 'right' });
  drawDocGlyph(doc, 'receipt', M + bandW / 2, cy, 13, [255, 255, 255]);
  drawWatermark(doc, 'receipt');

  // Company details
  let y = 48;
  doc.setTextColor(...GRAY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  [company.physicalAddress, company.email, company.phones?.join('  ·  '), company.tin ? `TIN: ${company.tin}` : '']
    .filter(Boolean)
    .forEach((l) => {
      doc.text(l as string, M, y);
      y += 5;
    });

  // Meta (right)
  let my = 48;
  doc.setFontSize(11);
  ([
    ['Receipt no.', p.receiptNumber],
    ['Date', fmtDate(p.date)],
    ['Method', METHOD_LABEL[p.method]],
  ] as Array<[string, string]>).forEach(([k, v]) => {
    doc.setTextColor(...GRAY);
    doc.setFont('helvetica', 'normal');
    doc.text(k, right - 60, my);
    doc.setTextColor(...CHAR);
    doc.setFont('helvetica', 'bold');
    doc.text(v, right, my, { align: 'right' });
    my += 5.5;
  });

  // Received from
  y = Math.max(y, my) + 8;
  doc.setTextColor(...GRAY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('RECEIVED FROM', M, y);
  y += 5.5;
  doc.setTextColor(...CHAR);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(p.clientName, M, y);
  y += 10;

  // Amount box
  doc.setFillColor(255, 241, 234);
  doc.roundedRect(M, y, right - M, 22, 2, 2, 'F');
  doc.setTextColor(...GRAY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text('Amount received', M + 6, y + 9);
  doc.setTextColor(...CHAR);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(formatPula(p.amount), right - 6, y + 14, { align: 'right' });
  y += 32;

  // Applied-to line
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...CHAR);
  doc.text(`Being payment towards invoice ${p.invoiceNumber}.`, M, y);
  y += 6.5;
  if (p.reference) {
    doc.setTextColor(...GRAY);
    doc.setFontSize(10.5);
    doc.text(`Reference: ${p.reference}`, M, y);
    y += 5.5;
  }
  if (p.notes) {
    doc.setTextColor(...GRAY);
    doc.setFontSize(10.5);
    const nl = doc.splitTextToSize(p.notes, right - M) as string[];
    doc.text(nl, M, y);
  }

  // Footer
  doc.setFontSize(10.5);
  doc.setTextColor(...GRAY);
  doc.text('Thank you for your payment.', M, 285);
  return doc;
}

export function downloadReceiptPdf(p: Payment, company: CompanySettings): void {
  buildReceiptPdf(p, company).save(receiptFileName(p));
}
export function receiptPdfBase64(p: Payment, company: CompanySettings): string {
  const uri = buildReceiptPdf(p, company).output('datauristring');
  return uri.slice(uri.indexOf(',') + 1);
}
